import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, Stack, Alert, CircularProgress, TextField, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  DialogTitle, DialogContent, DialogActions, MenuItem, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { apiUrl, handleApiError } from '../utils/api';
import NoMatchingRecords from '../components/NoMatchingRecords';
import AppDialog from '../components/AppDialog';
import { useConfirm } from '../components/ConfirmProvider';
import { useNotify } from '../components/NotificationProvider';

const base = `${apiUrl}/api/master/plant-payment-configs`;
// Both %s are stored as a fraction (0.10); the screen edits them as a percent (10).
const toPct = (frac) => (frac == null || frac === '' ? '' : (Number(frac) * 100).toString());
const toFrac = (pct) => (pct === '' || pct == null ? 0 : Number(pct) / 100);

const EMPTY = {
  matcher: '', label: '', deductionPct: '', fuelSurchargePct: '',
  invoiceTemplate: 'DEFAULT', rentAmount: '', billTo: '', remitTo: '',
};

export default function PlantPaymentConfigs() {
  const [rows, setRows] = useState([]);
  // {code: label} of the settlement sheets the backend can render.
  const [templates, setTemplates] = useState({ DEFAULT: 'Generic settlement' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  // Object URL of the stored logo while the dialog is open (null = none uploaded).
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const confirm = useConfirm();
  const notify = useNotify();

  // Fetched as a blob rather than pointed at with <img src>, so the tenant header the axios
  // interceptor adds is sent — a bare <img> request would carry no identity.
  const loadLogo = async (id) => {
    setLogoPreview((url) => { if (url) URL.revokeObjectURL(url); return null; });
    if (!id) return;
    try {
      const res = await axios.get(`${base}/${id}/logo`, { responseType: 'blob' });
      setLogoPreview(URL.createObjectURL(res.data));
    } catch {
      setLogoPreview(null); // 404 = no logo yet
    }
  };

  const uploadLogo = async (file) => {
    if (!file || !editing) return;
    setLogoBusy(true);
    try {
      const data = new FormData();
      data.append('file', file);
      await axios.post(`${base}/${editing.id}/logo`, data);
      await loadLogo(editing.id);
      await load();
      notify.success('Logo uploaded');
    } catch (err) {
      notify.error(handleApiError(err, 'Logo upload failed'));
    } finally {
      setLogoBusy(false);
    }
  };

  const removeLogo = async () => {
    if (!editing) return;
    setLogoBusy(true);
    try {
      await axios.delete(`${base}/${editing.id}/logo`);
      await loadLogo(editing.id);
      await load();
      notify.success('Logo removed');
    } catch (err) {
      notify.error(handleApiError(err, 'Could not remove the logo'));
    } finally {
      setLogoBusy(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(base);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(handleApiError(err, 'Failed to load plant payment configs'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    // Non-fatal: if the template list can't be fetched the dropdown still offers the generic sheet.
    axios.get(`${base}/templates`)
      .then((res) => { if (res.data && typeof res.data === 'object') setTemplates(res.data); })
      .catch(() => {});
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); loadLogo(null); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    loadLogo(r.id);
    setForm({
      matcher: r.matcher ?? '',
      label: r.label ?? '',
      deductionPct: toPct(r.deductionPct),
      fuelSurchargePct: toPct(r.fuelSurchargePct),
      invoiceTemplate: r.invoiceTemplate || 'DEFAULT',
      rentAmount: r.rentAmount == null || Number(r.rentAmount) === 0 ? '' : String(r.rentAmount),
      billTo: r.billTo ?? '',
      remitTo: r.remitTo ?? '',
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.matcher.trim()) { notify.warning('Matcher is required (e.g. "cemex").'); return; }
    if (!form.label.trim()) { notify.warning('Label is required.'); return; }
    const payload = {
      matcher: form.matcher.trim(),
      label: form.label.trim(),
      deductionPct: toFrac(form.deductionPct),
      fuelSurchargePct: toFrac(form.fuelSurchargePct),
      invoiceTemplate: form.invoiceTemplate || 'DEFAULT',
      rentAmount: form.rentAmount === '' ? 0 : Number(form.rentAmount),
      billTo: form.billTo.trim(),
      remitTo: form.remitTo.trim(),
    };
    try {
      if (editing) await axios.put(`${base}/${editing.id}`, payload);
      else await axios.post(base, payload);
      setDialogOpen(false);
      await load();
      notify.success(`Plant config ${editing ? 'updated' : 'created'}`);
    } catch (err) {
      notify.error(handleApiError(err, 'Save failed'));
    }
  };

  const remove = async (r) => {
    const ok = await confirm({
      title: 'Delete plant config',
      message: `Delete the payment rule for "${r.label}"? Its tickets will fall back to 0% deduction and no surcharge.`,
      confirmText: 'Delete', severity: 'error',
    });
    if (!ok) return;
    try {
      await axios.delete(`${base}/${r.id}`);
      await load();
      notify.success('Plant config deleted');
    } catch (err) {
      notify.error(handleApiError(err, 'Delete failed'));
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>Plant Payment Config</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        How each plant pays. <b>Dispatcher deduction %</b> is the dispatcher&apos;s commission taken off a
        ticket&apos;s revenue (CEMEX 10%, LCR 8.75%) → that gives the <i>Total after company</i>.
        <b>Fuel surcharge %</b> is applied to revenue and added to the driver&apos;s base <i>after</i> the
        dispatcher cut: driver base = (revenue − dispatcher%) + revenue × fuel%. A plant is matched to a
        ticket by the <b>Matcher</b> text in the ticket&apos;s company name. Each row also carries the
        <b> settlement sheet</b> that dispatcher wants its invoice printed on — choose the dispatcher on
        Settlements to use it.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">({rows.length} plants)</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Plant</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading && <CircularProgress />}
      {!loading && !error && rows.length === 0 && <NoMatchingRecords sx={{ mb: 2 }} />}

      {rows.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Plant / Label</TableCell>
                <TableCell>Matcher (in company name)</TableCell>
                <TableCell align="right">Dispatcher deduction %</TableCell>
                <TableCell align="right">Fuel surcharge %</TableCell>
                <TableCell align="right">Rent</TableCell>
                <TableCell>Settlement sheet</TableCell>
                <TableCell align="center">Logo</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.label}</TableCell>
                  <TableCell><code>{r.matcher}</code></TableCell>
                  <TableCell align="right">{toPct(r.deductionPct) || '0'}%</TableCell>
                  <TableCell align="right">{toPct(r.fuelSurchargePct) || '0'}%</TableCell>
                  <TableCell align="right">
                    {r.rentAmount ? `$${Number(r.rentAmount).toFixed(2)}` : '—'}
                  </TableCell>
                  <TableCell>{templates[r.invoiceTemplate] || r.invoiceTemplate || 'Generic settlement'}</TableCell>
                  <TableCell align="center">{r.hasLogo ? 'Yes' : '—'}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => remove(r)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AppDialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit Plant Payment Rule' : 'Add Plant Payment Rule'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Label" value={form.label} required fullWidth
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. CEMEX" />
            <TextField label="Matcher" value={form.matcher} required fullWidth
              onChange={(e) => setForm({ ...form, matcher: e.target.value })}
              helperText="Lowercase text that appears in the ticket's company name (e.g. cemex, martin, colorado)."
              placeholder="cemex" />
            <TextField label="Dispatcher deduction %" type="number" value={form.deductionPct} fullWidth
              onChange={(e) => setForm({ ...form, deductionPct: e.target.value })}
              helperText="The dispatcher's commission taken off revenue (e.g. CEMEX 10, LCR 8.75). 0 = none."
              InputProps={{ endAdornment: <span>%</span> }} />
            <TextField label="Fuel surcharge %" type="number" value={form.fuelSurchargePct} fullWidth
              onChange={(e) => setForm({ ...form, fuelSurchargePct: e.target.value })}
              helperText="Applied to revenue and added to the driver's base AFTER the dispatcher cut. 0 = none."
              InputProps={{ endAdornment: <span>%</span> }} />

            <Divider textAlign="left" sx={{ pt: 1 }}>Settlement sheet</Divider>
            <Typography variant="body2" color="text.secondary">
              How this dispatcher wants its settlement printed. Pick their form on Settlements and the
              invoice comes out on it, with the deductions below in the footer.
            </Typography>
            <TextField select label="Invoice template" value={form.invoiceTemplate} fullWidth
              onChange={(e) => setForm({ ...form, invoiceTemplate: e.target.value })}>
              {Object.entries(templates).map(([code, name]) => (
                <MenuItem key={code} value={code}>{name}</MenuItem>
              ))}
            </TextField>
            <TextField label="Weekly truck rent" type="number" value={form.rentAmount} fullWidth
              onChange={(e) => setForm({ ...form, rentAmount: e.target.value })}
              helperText="Deducted in the settlement footer (Bobcat's 'Rent' line). Blank / 0 = none."
              InputProps={{ startAdornment: <span>$&nbsp;</span> }} />
            <TextField label="Bill to" value={form.billTo} fullWidth multiline minRows={2}
              onChange={(e) => setForm({ ...form, billTo: e.target.value })}
              helperText="Printed in the sheet's 'Bill to' block — one line per line (e.g. name, street, city)."
              placeholder={'Melendrez\n11608 Moore Rd\nAustin, TX 78719'} />
            <TextField label="Payment to / remit" value={form.remitTo} fullWidth
              onChange={(e) => setForm({ ...form, remitTo: e.target.value })}
              helperText="Contact or phone shown in the 'Payment to' block." />

            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Letterhead logo</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Printed at the top of their sheet (PNG/JPG/GIF, max 2MB). Without one the sheet falls back
                to the dispatcher&apos;s name in bold.
                {!editing && ' Save this dispatcher first, then re-open it to upload.'}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                {logoPreview && (
                  <Box component="img" src={logoPreview} alt="Logo"
                    sx={{ maxHeight: 56, maxWidth: 160, border: '1px solid #ddd', p: 0.5 }} />
                )}
                <Button variant="outlined" component="label" disabled={!editing || logoBusy}>
                  {logoPreview ? 'Replace' : 'Upload'}
                  <input hidden type="file" accept="image/png,image/jpeg,image/gif"
                    onChange={(e) => uploadLogo(e.target.files?.[0])} />
                </Button>
                {logoPreview && (
                  <Button color="error" onClick={removeLogo} disabled={logoBusy}>Remove</Button>
                )}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}>Save</Button>
        </DialogActions>
      </AppDialog>
    </Box>
  );
}
